<template>
  <v-card
    hover
    @drop="onDrop"
    @dragover="onDragEnter"
    @dragexit="onDragExit"
    @dragleave="onDragExit"
    class="d-flex align-center px-4"
  >
    <v-btn
      icon
      @click="onClick"
      @mouseenter="onDragEnter"
      @mouseleave="onDragExit"
    >
      <v-icon
        v-if="isOver"
        color="accent"
      >
        mdi-folder-open
      </v-icon>
      <v-icon
        v-else
      >
        mdi-folder
      </v-icon>
    </v-btn>
    <v-card-text
      @click="onClick"
      @mouseenter="onDragEnter"
      @mouseleave="onDragExit"
      class="text-break text-truncate"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <span v-on="on" class="title">{{project["nm_project"]}}</span>
        </template>
        <span>{{project["nm_project"]}}</span>
      </v-tooltip>
    </v-card-text>
    <v-card-actions class="pa-0">
      <v-spacer/>
      <v-menu
        offset-y
        transition="scale-transition"
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on: menu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                color="primary"
                icon
                elevation="0"
                v-on="{...menu, ...tooltip}"
              >
                <v-icon>mdi-menu</v-icon>
              </v-btn>
            </template>
            <span>Mais ações</span>
          </v-tooltip>
        </template>
        <v-list
          rounded
          dense
          flat
        >
          <v-subheader>Ações</v-subheader>
          <v-list-item-group
            color="primary"
          >
            <project-share-dialog
              :project="project"
            >
              <template v-slot:activator="{ on }">
                <v-list-item
                  v-on="on"
                >
                  <v-list-item-icon>
                    <v-icon>{{$vuetify.icons.values["mdiShareVariant"]}}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Compartilhar</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </project-share-dialog>
            <project-edit-dialog
              :project="project"
            >
              <template v-slot:activator="{ on }">
                <v-list-item
                  v-on="on"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Editar</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </project-edit-dialog>
            <v-list-item
              :disabled="deleteButtonIsDisabled || isDeleting"
              :color="!deleteButtonIsDisabled ? 'error' : ''"
              @click="onDeleteClick"
            >
              <v-list-item-icon>
                <v-progress-circular
                  v-if="isDeleting"
                  indeterminate
                  color="error"
                  size="20"
                >
                </v-progress-circular>
                <v-icon v-else :color="!deleteButtonIsDisabled ? 'error' : ''">mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Excluir</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-card-actions>
  </v-card>
</template>

<script>
  import {DIALOG_TYPES, DialogOptions} from "../../helpers/dialogs"
  import {GET_PROCESSING_LIST, MOVE_PROCESSING} from "../../store/modules/processing"
  import {mapActions, mapGetters} from "vuex"
  import {DELETE_PROJECT} from "../../store/modules/project"
  import ProjectShareDialog from "./ProjectShareDialog"
  import ProjectEditDialog from "./Configuration/ProjectEditDialog"
  import {filter, some} from "lodash/collection"
  import {isEqual} from "lodash/lang"
  import {PROCESSING_STATUS} from "../../helpers/processings/constants"

  export default {
    name: "ProjectFolder",
    components: {ProjectEditDialog, ProjectShareDialog},
    props: {
      project: {
        type: Object,
        required: true,
      },
      cdProject: {
        type: Number,
        validator: prop => typeof prop === "number" || prop === null,
      }
    },
    data: () => ({
      isOver: false,
      isDeleting: false,
      idProcessingToMove: null,
    }),
    methods: {
      onClick() {
        this.$emit("update:cdProject", this.project["id_project"])
      },
      onDrop(e) {
        this.idProcessingToMove = e.dataTransfer.getData("idProcessing")
        const nmProcessing = e.dataTransfer.getData("nmProcessing")
        this.$dialog(new DialogOptions({
          title: "Mover processamento",
          content: `Você tem certeza que deseja mover o processamento <b>${nmProcessing}</b> para o projeto <b>${this.project["nm_project"]}</b>?`,
          onConfirm: this.updateDroppedProcessing,
          onCancel: this.onDragExit,
          type: DIALOG_TYPES.CONFIRM,
        }))
      },
      onDragEnter(e) {
        e.preventDefault()
        this.isOver = true
      },
      onDragExit() {
        this.isOver = false
      },
      updateDroppedProcessing() {
        this._moveProcessing({
            idProcessing: this.idProcessingToMove,
            cdProject: this.project["id_project"],
          })
          .finally(() => {
            this.isOver = false
          })
      },
      onDeleteClick() {
        this.$dialog(new DialogOptions({
          title: "Excluir pasta",
          content: "Você tem certeza que deseja excluir está pasta e <b>todos os processamentos</b> atribuídos a mesma?",
          onConfirm: this.deleteProjectHandler,
          type: DIALOG_TYPES.CONFIRM,
        }))
      },
      deleteProjectHandler() {
        this.isDeleting = true
        this._deleteProject(this.project["id_project"])
          .finally(() => {
            this.isDeleting = false
          })
      },
      ...mapActions({
        _deleteProject: DELETE_PROJECT,
        _moveProcessing: MOVE_PROCESSING,
      }),
    },
    computed: {
      deleteButtonIsDisabled() {
        if (!this._processingList) return true

        const processingList = filter(this._processingList, processing => isEqual(processing["cd_project"], this.project["id_project"]))
        return some(processingList, processing => isEqual(processing["ab_processing_status"], PROCESSING_STATUS.PROCESSING))
      },
      ...mapGetters({
        _processingList: GET_PROCESSING_LIST
      })
    }
  }
</script>

<style scoped>

</style>
